/**
 * FormData is responsible for handling all state related to Pages, Elements, Settings, and Styles.
 *
 * Note: This file is relatively complex, and could probably be broken down further.
 *
 * The <FormData/> component sets up several contexts:
 * - <SettingContext/>
 * - <ElementContext/>,
 * - <StyleContext/>
 * - <PageContext/>
 * - <EditingContext/>
 * - <UnsavedChangesContext/>
 *
 * They are set up separately so that they can be used individually, and to save unnecessary re-renders:
 * components that don't need data from one won't rerender because another has changed.
 *
 * These contexts are all hooked up to the same Reducer, which is our source of truth for the Builder.
 * The data managed by this reducer is serialized and sent to the server to be persisted when the user
 * saves their work.
 *
 * This file also defines several useful helper Hooks for updating individual styles, elements,
 * and other form data.
 *
 * == Example of using a helper hook
 * const useElement = createIndividualObjectHook("elements", ElementContext)
 *
 * === In component
 * const [element, updateElement] = useElement("header", { content: "Join the Newsletter"})
 *
 * `useElement` will find an element with the name "header", or create one
 * if one does not exist.
 *
 * If `defaults` are provided, it will merge those defaults in with the
 * element and update it.
 *
 * `useStyle` and `useSettings` also follow this pattern.
 */

import React, { useReducer, useContext, useEffect } from "react"
import PropTypes from "prop-types"
import { snakeCase } from "lodash"
import { byName, custom, inputs, active, withoutNull } from "./filters"

import { reducer } from "./reducer"
import { useTemplate } from "../utils/use-template"
import { templates as allTemplates } from "../templates"
import {
  useContainer,
  useContainerState,
  ContainerContext
} from "../use-container"
import { StyleContext } from "./style-context"

export const FormContext = React.createContext({ form: {}, dispatch: () => {} })
export const EditingContext = React.createContext(false)

const UnsavedChangesContext = React.createContext({
  unsavedChanges: false,
  dispatch: () => {}
})
const PageContext = React.createContext({ pages: [], dispatch: () => {} })
const SettingContext = React.createContext({ settings: [], dispatch: () => {} })
const ElementContext = React.createContext({ elements: [], dispatch: () => {} })

export const useElements = () => {
  const { elements, dispatch } = useContext(ElementContext)

  const update = attributes => {
    dispatch({ type: "update_element", payload: attributes })
  }

  const remove = attributes => {
    update({ ...attributes, _delete: true })

    // Remove this element's children.
    elements
      .filter(el => el.parent_element_id === attributes.name && !el._delete)
      .map(child => remove({ name: child.element_id }))
  }

  return {
    all: elements.filter(withoutNull),
    custom: elements.filter(active).filter(custom),
    inputs: elements.filter(active).filter(inputs),
    update,
    remove
  }
}

export const useElement = (name, defaults) => {
  const { elements, dispatch } = useContext(ElementContext)

  const update = attributes => {
    dispatch({
      type: "update_element",
      payload: attributes
    })
  }

  let element = elements.find(byName(name)) || { name }
  if (defaults) element = { ...defaults, ...element }

  useEffect(() => {
    if (name && defaults) update(element)
  }, [])

  if (!name) return [null, () => {}]

  return [element, update]
}

export const useStyle = name => {
  const { styles, dispatch } = useContext(StyleContext)

  const update = attributes => {
    dispatch({
      type: "update_style",
      payload: attributes
    })
  }

  let style = styles.find(byName(name)) || { name }

  if (!name) return [null, () => {}]

  return [style, update]
}

export const useSetting = (name, defaults) => {
  const { settings, dispatch } = useContext(SettingContext)

  const update = attributes => {
    dispatch({
      type: "update_setting",
      payload: attributes
    })
  }

  let setting = settings.find(byName(name)) || { name }
  if (defaults) setting = { ...defaults, ...setting }

  useEffect(() => {
    if (name && defaults) {
      update(setting)
    }
  }, [])

  if (!name) return [null, () => {}]

  return [setting, update]
}

export const useSettings = () => {
  const { settings } = useContext(SettingContext)

  const serialize = settingNames =>
    settings.reduce((settingsObject, setting) => {
      if (settingNames && !settingNames.includes(setting.name)) {
        return settingsObject
      }
      const { name, ...serializedSetting } = setting
      settingsObject[snakeCase(name)] = serializedSetting
      return settingsObject
    }, {})

  return { all: settings, serialize }
}

export const useEditingContext = () => {
  return useContext(EditingContext)
}

export const useUnsavedChanges = () => {
  const { unsavedChanges, dispatch } = useContext(UnsavedChangesContext)

  const setUnsavedChanges = bool => {
    dispatch({
      type: "set_unsaved_changes",
      payload: bool
    })
  }
  return { setUnsavedChanges, unsavedChanges }
}

export const useFormInfo = () => {
  const container = useContainer()
  const { form, dispatch, templateComponent } = useContext(FormContext)
  const { editing, action, id, uid, metadata, ckjsVersion, htmlUrl, url } = form

  const setCurrentPage = pageId => {
    dispatch({
      type: "set_current_page",
      payload: { pageId }
    })
  }

  const setMetadata = metadata => {
    dispatch({
      type: "set_metadata",
      payload: { metadata }
    })
  }

  const currentPage = form.pages.find(page => page.id === form.currentPageId)

  const template = container.template

  return {
    editing,
    action,
    id,
    uid,
    template,
    metadata,
    setCurrentPage,
    setMetadata,
    currentPage,
    form,
    templateComponent,
    ckjsVersion,
    url,
    htmlUrl
  }
}

export const usePages = () => {
  const { pages, dispatch } = useContext(PageContext)

  const update = (pageId, attributes) => {
    dispatch({ type: "set_unsaved_changes", payload: true })
    dispatch({
      type: "update_page",
      payload: { pageId, ...attributes }
    })
  }

  const remove = pageId => {
    dispatch({
      type: "remove_page",
      payload: { pageId }
    })
  }

  return { pages, updatePage: update, removePage: remove }
}

export const FormData = ({
  children,
  initialPages,
  initialSettings,
  editing,
  action,
  id,
  uid,
  url,
  htmlUrl,
  metadata,
  template,
  currentPageId,
  ckjsVersion,
  htmlBlocksAllowed,
  templates = allTemplates
}) => {
  const [form, dispatch] = useReducer(reducer, {
    currentPageId: currentPageId || initialPages[0].id,
    settings: initialSettings,
    pages: initialPages,
    editing,
    action,
    id,
    uid,
    url,
    htmlUrl,
    metadata,
    ckjsVersion
  })

  const container = useContainerState({
    id,
    template,
    htmlBlocksAllowed
  })

  const page = form.pages.find(page => page.id === form.currentPageId)
  const templateComponent = useTemplate(
    {
      name: container.template.name,
      url: container.template.url,
      layout: page.layout
    },
    templates
  )

  // Sets the default styles for a landing page when the template code loads.
  // It will run anytime the templateComponent changes, like when creating a thank you page.
  useEffect(() => {
    if (templateComponent) {
      templateComponent.settings.forEach(defaults => {
        const { selector, type, value } = defaults
        const name = `${selector}.${type}`

        const update = attributes => {
          dispatch({
            type: "update_style",
            payload: attributes
          })
        }

        let style = page.styles.find(byName(name))
        if (!style) {
          update({ name, value })
        }
      })
    }
  }, [templateComponent])

  return (
    <ContainerContext.Provider value={container}>
      <FormContext.Provider value={{ form, templateComponent, dispatch }}>
        <UnsavedChangesContext.Provider
          value={{ unsavedChanges: form.unsavedChanges || false, dispatch }}
        >
          <EditingContext.Provider value={editing}>
            <PageContext.Provider value={{ pages: form.pages, dispatch }}>
              <ElementContext.Provider
                value={{
                  elements: page.elements,
                  dispatch
                }}
              >
                <StyleContext.Provider
                  value={{ styles: page.styles, dispatch }}
                >
                  <SettingContext.Provider
                    value={{ settings: form.settings, dispatch: dispatch }}
                  >
                    {children}
                  </SettingContext.Provider>
                </StyleContext.Provider>
              </ElementContext.Provider>
            </PageContext.Provider>
          </EditingContext.Provider>
        </UnsavedChangesContext.Provider>
      </FormContext.Provider>
    </ContainerContext.Provider>
  )
}

FormData.propTypes = {
  children: PropTypes.node,
  initialSettings: PropTypes.arrayOf(PropTypes.object).isRequired,
  initialPages: PropTypes.arrayOf(PropTypes.object).isRequired,
  editing: PropTypes.bool,
  action: PropTypes.string,
  id: PropTypes.number,
  uid: PropTypes.string.isRequired,
  metadata: PropTypes.object,
  template: PropTypes.object,
  currentPageId: PropTypes.number,
  ckjsVersion: PropTypes.string,
  templates: PropTypes.array,
  url: PropTypes.string,
  htmlUrl: PropTypes.string,
  htmlBlocksAllowed: PropTypes.bool
}
